import { Controller } from "@hotwired/stimulus"

// Interactive offer-sizing card. Every field is an editable input — auto-filled from the
// statement affordability picture when the lead has analysis, otherwise defaulted from funder
// policy / left blank for manual entry (all still editable, incl. the amount & factor limits).
// The underwriter sets the affordable payment, holdback, term, factor bounds and advance
// limits, drags the factor, and toggles daily/weekly to size the advance:
//
//   payback = payment_per_period × payment_count      (count derived from term & frequency)
//   advance = round(payback / factor to $500), clamped to [minAmount, maxAmount]
//   weekly view: payment ×5, count = termPayments / 5 (5 business days/week)
//
// "Use these terms" pushes the current sizing into the offer form so it stays editable.
export default class extends Controller {
  static values = {
    businessDaysPerWeek: { type: Number, default: 5 },
  }

  static targets = [
    "dailyPayment", "holdback", "termPayments", "minFactor", "maxFactor", "minAmount", "maxAmount",
    "factor", "factorLabel", "factorMin", "factorMax",
    "payment", "paymentUnit", "advance", "advanceNote", "payback", "term", "cost",
    "dailyBtn", "weeklyBtn",
  ]

  connect() {
    this.frequency = "daily"
    this.syncFactorBounds()
    this.render()
  }

  setDaily() { this.frequency = "daily"; this.render() }
  setWeekly() { this.frequency = "weekly"; this.render() }
  onInput() { this.render() }
  onFactorInput() { this.render() }
  onFactorBoundsInput() { this.syncFactorBounds(); this.render() }

  // Re-bound the factor slider to the (editable) min/max factor inputs, keep the printed
  // endpoints in sync, and clamp the slider's current value into the new range.
  syncFactorBounds() {
    const min = this.num(this.minFactorTarget), max = this.num(this.maxFactorTarget)
    if (min > 0) { this.factorTarget.min = min; if (this.hasFactorMinTarget) this.factorMinTarget.textContent = min }
    if (max > 0) { this.factorTarget.max = max; if (this.hasFactorMaxTarget) this.factorMaxTarget.textContent = max }
    const v = this.num(this.factorTarget)
    if (min > 0 && v < min) this.factorTarget.value = min
    if (max > 0 && v > max) this.factorTarget.value = max
  }

  // --- inputs ----------------------------------------------------------------------
  num(el) { const n = parseFloat(el && el.value); return Number.isFinite(n) ? n : 0 }
  get dailyPayment() { return this.num(this.dailyPaymentTarget) }
  get termPayments() { return this.num(this.termPaymentsTarget) || 120 }
  get holdback() { return this.num(this.holdbackTarget) }
  get factorRate() { return this.num(this.factorTarget) }
  get minAmount() { return this.num(this.minAmountTarget) }
  get maxAmount() { const m = this.num(this.maxAmountTarget); return m > 0 ? m : Infinity }
  get weekly() { return this.frequency === "weekly" }

  // --- derived numbers -------------------------------------------------------------
  get paybackTotal() { return this.dailyPayment * this.termPayments }
  get paymentCount() {
    return this.weekly ? Math.max(1, Math.round(this.termPayments / this.businessDaysPerWeekValue)) : this.termPayments
  }
  get paymentAmount() {
    return this.weekly ? this.dailyPayment * this.businessDaysPerWeekValue : this.dailyPayment
  }
  // Advance the payback buys at this factor, rounded to $500 and clamped to the (editable) limits.
  get rawAdvance() { return this.factorRate > 0 ? this.paybackTotal / this.factorRate : 0 }
  get advanceAmount() {
    if (this.rawAdvance <= 0) return 0
    const rounded = Math.round(this.rawAdvance / 500) * 500
    return Math.min(Math.max(rounded, this.minAmount), this.maxAmount)
  }
  get capped() {
    if (this.rawAdvance <= 0) return false
    const rounded = Math.round(this.rawAdvance / 500) * 500
    return this.advanceAmount !== rounded
  }

  render() {
    this.factorLabelTarget.textContent = this.factorRate.toFixed(2)
    this.paymentTarget.textContent = this.money(this.paymentAmount)
    this.paymentUnitTarget.textContent = this.weekly ? "/week" : "/day"
    this.advanceTarget.textContent = this.money(this.advanceAmount, 0)
    this.paybackTarget.textContent = this.money(this.paybackTotal, 0)
    this.termTarget.textContent = `${this.paymentCount} ${this.weekly ? "weekly" : "daily"} payments`
    this.costTarget.textContent = this.money(this.paybackTotal - this.advanceAmount, 0)

    if (this.hasAdvanceNoteTarget) this.advanceNoteTarget.classList.toggle("hidden", !this.capped)
    this.dailyBtnTarget.classList.toggle("btn-active", !this.weekly)
    this.weeklyBtnTarget.classList.toggle("btn-active", this.weekly)
  }

  // Populate the offer form (same element the "Suggest terms" button fills) and let its own
  // listeners recompute. Amount → factor → term → frequency → payment → holdback.
  useTerms() {
    const form = document.querySelector("#offers form")
    if (!form) return
    const set = (name, value) => {
      const el = form.querySelector(`[name="offer[${name}]"]`)
      if (!el || value == null) return
      el.value = value
      el.dispatchEvent(new Event("input", { bubbles: true }))
    }
    set("amount", this.advanceAmount)
    set("factor_rate", this.factorRate.toFixed(2))
    set("term_value", this.paymentCount)
    set("term_unit", "payments")
    set("payment_frequency", this.frequency)
    set("payment_amount", this.paymentAmount.toFixed(2))
    if (this.holdback > 0) set("holdback_percent", this.holdback)
    form.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  money(n, dp = 2) {
    return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })
  }
}
